// ============================================================
// Distribution Rules Page — History-based layout
// Sections: Active Rule · Scheduled Rules · Previous Rules · Drafts
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import RuleTypeSelector from "../../components/distribution/RuleTypeSelector";
import UniversalOptionsPanel from "../../components/distribution/UniversalOptionsPanel";
import SeniorityBandsModal from "../../components/distribution/SeniorityBandsModal";
import {
  useDistributionRules,
  useDistributionRuleMutations,
} from "../../hooks/useDistributionRules";
import { useJobRoles } from "../../hooks/useJobRoles";
import { useTenantContext } from "../../contexts/TenantContext";
import { distributionRulesApi, seniorityBandsApi } from "../../services/api";

// ── Constants ─────────────────────────────────────────────────────────────
const TYPE_MAP = { Equal: 0, RoleScore: 1, ByDepartment: 2 };
const TYPE_NAMES = {
  0: "Equal Distribution",
  1: "By Job Role Score",
  2: "By Department",
};
const TYPES_REQUIRING_ALLOCATION = [1, 2];
const DEFAULT_DEPARTMENTS = [
  { id: "FOH", name: "Front of House (FOH)" },
  { id: "BOH", name: "Back of House (BOH)" },
  { id: "Bar", name: "Bar" },
  { id: "Kitchen", name: "Kitchen" },
  { id: "Admin", name: "Administration" },
];

const getTomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const parseRuleType = (type) => {
  if (typeof type === "number") {
    return type > 2 ? 0 : type;
  }
  return TYPE_MAP[type] ?? 0;
};

const blankFormData = () => ({
  name: "",
  description: "",
  type: 0,
  priority: 100,
  allocations: [],
  isDraft: false,
  effectiveFrom: getTomorrow(),
  enableMinimumFloor: false,
  minimumFloorAmount: 0,
  enableHourFactor: false,
  hourFactorWeight: 0.5,
  enableSeniorityFactor: false,
  seniorityFactorWeight: 0.3,
  seniorityBands: [],
  seniorityBandsJson: null,
});

// ── Main Component ─────────────────────────────────────────────────────────
const RuleManager = () => {
  const navigate = useNavigate();

  const { selectedTenant, needsTenantSelection, isGlobalAdmin, userRole } =
    useTenantContext();

  const canModifyRules = isGlobalAdmin
    ? !!selectedTenant
    : ["TroncMaster", "TenantAdmin", "Manager"].includes(userRole);

  const shouldFetchData = !isGlobalAdmin || !!selectedTenant;

  // ── Data hooks ───────────────────────────────────────────────────────
  const { rules, loading, error, refetch } = useDistributionRules({
    autoFetch: shouldFetchData,
  });
  const {
    createRule,
    updateRule,
    deleteRule,
    loading: mutationLoading,
  } = useDistributionRuleMutations();
  const {
    jobRoles,
    loading: jobRolesLoading,
    refetch: refetchJobRoles,
  } = useJobRoles({ autoFetch: shouldFetchData, activeOnly: true });

  const tenantJobRoles = jobRoles || [];

  // ── Page state ───────────────────────────────────────────────────────
  const [notification, setNotification] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [formData, setFormData] = useState(blankFormData());
  const [formMode, setFormMode] = useState(null); // 'create' | 'edit-scheduled' | 'edit-draft'
  const [formTargetId, setFormTargetId] = useState(null);
  const [loadingFormDetail, setLoadingFormDetail] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [showSeniorityModal, setShowSeniorityModal] = useState(false);
  const [tenantSeniorityBands, setTenantSeniorityBands] = useState([]);

  // ── Reload on tenant change ──────────────────────────────────────────
  useEffect(() => {
    if (shouldFetchData) {
      refetch();
      refetchJobRoles();
      loadTenantSeniorityBands();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant?.id, shouldFetchData]);

  const loadTenantSeniorityBands = async () => {
    if (!shouldFetchData) return;
    try {
      const res = await seniorityBandsApi.get();
      if (res.success && res.data?.bands)
        setTenantSeniorityBands(res.data.bands);
    } catch {
      /* ignore */
    }
  };

  // ── Utility ─────────────────────────────────────────────────────────
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // ── Classify rules ───────────────────────────────────────────────────
  const todayDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const activeRule = useMemo(
    () => rules.find((r) => r.isActive && !r.isDraft),
    [rules],
  );

  const scheduledRules = useMemo(
    () =>
      rules.filter((r) => {
        if (r.isActive || r.isDraft) return false;
        if (!r.effectiveFrom) return false;
        return new Date(r.effectiveFrom) > todayDate;
      }),
    [rules, todayDate],
  );

  const previousRules = useMemo(() => {
    const activeId = activeRule?.id;
    const scheduledIds = new Set(scheduledRules.map((r) => r.id));
    return rules.filter((r) => {
      if (r.isDraft) return false;
      if (r.id === activeId) return false;
      if (scheduledIds.has(r.id)) return false;
      return r.hasBeenDistributed || !!r.effectiveTo;
    });
  }, [rules, activeRule, scheduledRules]);

  const draftRules = useMemo(() => rules.filter((r) => r.isDraft), [rules]);

  // ── Departments ──────────────────────────────────────────────────────
  const departments = useMemo(() => {
    const depts = [
      ...new Set(tenantJobRoles.map((r) => r.department).filter(Boolean)),
    ];
    if (depts.length === 0) return DEFAULT_DEPARTMENTS;
    const nameMap = {
      FOH: "Front of House (FOH)",
      BOH: "Back of House (BOH)",
      Admin: "Administration",
      Management: "Management",
      Kitchen: "Kitchen (BOH)",
      Bar: "Bar (FOH)",
      Waitstaff: "Waitstaff (FOH)",
    };
    return depts
      .map((d) => ({ id: d, name: nameMap[d] || d }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tenantJobRoles]);

  // ── Expand detail ────────────────────────────────────────────────────
  const handleExpandDetail = useCallback(
    async (ruleId) => {
      if (expandedDetails[ruleId] && expandedDetails[ruleId] !== "loading") {
        setExpandedDetails((prev) => ({ ...prev, [ruleId]: null }));
        return;
      }
      setExpandedDetails((prev) => ({ ...prev, [ruleId]: "loading" }));
      try {
        const res = await distributionRulesApi.getById(ruleId);
        if (res.success && res.data) {
          setExpandedDetails((prev) => ({ ...prev, [ruleId]: res.data }));
        } else {
          setExpandedDetails((prev) => ({ ...prev, [ruleId]: null }));
        }
      } catch {
        setExpandedDetails((prev) => ({ ...prev, [ruleId]: null }));
      }
    },
    [expandedDetails],
  );

  // ── Form helpers ─────────────────────────────────────────────────────
  const isFirstNonDraftRule = rules.filter((r) => !r.isDraft).length === 0;

  const handleCreateNew = () => {
    setFormData({
      ...blankFormData(),
      effectiveFrom: isFirstNonDraftRule ? "" : getTomorrow(),
    });
    setFormMode("create");
    setFormTargetId(null);
    setShowNewForm(true);
  };

  const openEditForm = async (rule, mode) => {
    setLoadingFormDetail(true);
    setFormMode(mode);
    setFormTargetId(rule.id);
    setShowNewForm(true);
    try {
      const res = await distributionRulesApi.getById(rule.id);
      if (res.success && res.data) {
        const d = res.data;
        const bands = d.seniorityBandsJson
          ? (() => {
              try {
                return JSON.parse(d.seniorityBandsJson);
              } catch {
                return [];
              }
            })()
          : d.seniorityBands || [];
        setFormData({
          name: d.name,
          description: d.description || "",
          type: parseRuleType(d.type),
          priority: d.priority || 100,
          isDraft: mode === "edit-draft",
          effectiveFrom:
            mode === "edit-draft"
              ? ""
              : d.effectiveFrom
                ? new Date(d.effectiveFrom).toISOString().split("T")[0]
                : getTomorrow(),
          allocations: (d.allocations || []).map((a) => ({
            jobRoleId: a.jobRoleId,
            department: a.department,
            scoreMultiplier: parseFloat(a.scoreMultiplier) || 1.0,
            sortOrder: a.sortOrder || 0,
          })),
          enableMinimumFloor: d.enableMinimumFloor || false,
          minimumFloorAmount: d.minimumFloorAmount || 0,
          enableHourFactor: d.enableHourFactor || false,
          hourFactorWeight: d.hourFactorWeight ?? 0.5,
          enableSeniorityFactor: d.enableSeniorityFactor || false,
          seniorityFactorWeight: d.seniorityFactorWeight ?? 0.3,
          seniorityBands: bands,
          seniorityBandsJson: d.seniorityBandsJson || null,
        });
      }
    } catch {
      showNotification("Failed to load rule details", "error");
    } finally {
      setLoadingFormDetail(false);
    }
  };

  const handleCancelForm = () => {
    setShowNewForm(false);
    setFormMode(null);
    setFormTargetId(null);
    setFormData(blankFormData());
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (type) => {
    const prevReq = TYPES_REQUIRING_ALLOCATION.includes(formData.type);
    const newReq = TYPES_REQUIRING_ALLOCATION.includes(type);
    const shouldClear = !newReq || (prevReq && formData.type !== type);
    setFormData((prev) => ({
      ...prev,
      type,
      allocations: shouldClear ? [] : prev.allocations,
    }));
  };

  const handleUniversalOptionsChange = (opts) => {
    setFormData((prev) => ({ ...prev, ...opts }));
  };

  // ── Allocation helpers ───────────────────────────────────────────────
  const requiresAllocation = TYPES_REQUIRING_ALLOCATION.includes(formData.type);
  const isValidAllocations =
    !requiresAllocation || formData.allocations.length > 0;

  const handleAddAllocation = () => {
    if (formData.type === 1) {
      const usedIds = formData.allocations.map((a) => a.jobRoleId);
      const available = tenantJobRoles.find((r) => !usedIds.includes(r.id));
      if (!available) {
        showNotification("All job roles have been added", "warning");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        allocations: [
          ...prev.allocations,
          {
            jobRoleId: available.id,
            scoreMultiplier: 1.0,
            sortOrder: prev.allocations.length,
          },
        ],
      }));
    } else if (formData.type === 2) {
      const usedDepts = formData.allocations.map((a) => a.department);
      const available = departments.find((d) => !usedDepts.includes(d.id));
      if (!available) {
        showNotification("All departments have been added", "warning");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        allocations: [
          ...prev.allocations,
          {
            department: available.id,
            scoreMultiplier: 1.0,
            sortOrder: prev.allocations.length,
          },
        ],
      }));
    }
  };

  const handleRemoveAllocation = (idx) =>
    setFormData((prev) => ({
      ...prev,
      allocations: prev.allocations.filter((_, i) => i !== idx),
    }));

  const handleAllocationChange = (idx, field, value) =>
    setFormData((prev) => ({
      ...prev,
      allocations: prev.allocations.map((a, i) =>
        i === idx ? { ...a, [field]: value } : a,
      ),
    }));

  const resetMultipliers = () =>
    setFormData((prev) => ({
      ...prev,
      allocations: prev.allocations.map((a) => ({
        ...a,
        scoreMultiplier: 1.0,
      })),
    }));

  // ── Save ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.name.trim()) {
      showNotification("Rule name is required", "error");
      return;
    }
    if (requiresAllocation && !isValidAllocations) {
      showNotification("Please add at least one allocation", "error");
      return;
    }

    const isFirst =
      isFirstNonDraftRule && formMode === "create" && !formData.isDraft;

    if (!formData.isDraft && !isFirst) {
      if (!formData.effectiveFrom) {
        showNotification("Please select an effective from date", "error");
        return;
      }
      const sel = new Date(formData.effectiveFrom);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      sel.setHours(0, 0, 0, 0);
      if (sel < tomorrow) {
        showNotification("Effective date must be at least tomorrow", "error");
        return;
      }
    }

    const ruleData = {
      name: formData.name,
      description: formData.description || null,
      type: formData.type,
      priority: formData.priority,
      isDraft: formData.isDraft,
      effectiveFrom:
        !formData.isDraft && formData.effectiveFrom
          ? new Date(formData.effectiveFrom + "T00:00:00Z")
              .toISOString()
              .split(".")[0] + "Z"
          : null,
      effectiveTo: null,
      enableMinimumFloor: formData.enableMinimumFloor,
      minimumFloorAmount: formData.enableMinimumFloor
        ? formData.minimumFloorAmount
        : null,
      enableHourFactor: formData.enableHourFactor,
      hourFactorWeight: formData.hourFactorWeight,
      enableSeniorityFactor: formData.enableSeniorityFactor,
      seniorityFactorWeight: formData.seniorityFactorWeight,
      seniorityBands:
        formData.seniorityBands?.length > 0
          ? formData.seniorityBands
          : formData.seniorityBandsJson
            ? (() => {
                try {
                  return JSON.parse(formData.seniorityBandsJson);
                } catch {
                  return null;
                }
              })()
            : null,
      allocations: requiresAllocation
        ? formData.allocations.map((a, idx) => ({
            jobRoleId: formData.type === 1 ? a.jobRoleId : undefined,
            department: formData.type === 2 ? a.department : undefined,
            scoreMultiplier: parseFloat(a.scoreMultiplier) || 1.0,
            sortOrder: idx,
          }))
        : [],
    };

    try {
      if (formMode === "create") {
        await createRule(ruleData);
        showNotification(
          formData.isDraft
            ? "Draft rule created. Test it in the Distribution Lab."
            : isFirst
              ? "First distribution rule created and activated immediately."
              : `Rule scheduled — will become active from ${formatDate(
                  formData.effectiveFrom,
                )}.`,
          "success",
        );
      } else {
        await updateRule(formTargetId, {
          ...ruleData,
          updateReason: "Updated via Rule Manager",
        });
        showNotification(
          formData.isDraft ? "Draft rule updated." : "Scheduled rule updated.",
          "success",
        );
      }
      refetch();
      handleCancelForm();
    } catch (err) {
      showNotification(err.message || "Failed to save rule", "error");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDeleteRule = async (rule) => {
    if (rule.hasBeenDistributed) {
      showNotification(
        "Cannot delete a rule that has been used in distributions.",
        "error",
      );
      return;
    }
    if (rule.isActive) {
      showNotification("Cannot delete the active rule.", "error");
      return;
    }
    if (!confirm(`Delete "${rule.name}"?`)) return;
    try {
      await deleteRule(rule.id);
      showNotification("Rule deleted.");
      if (formTargetId === rule.id) handleCancelForm();
      refetch();
    } catch (err) {
      showNotification(err.message || "Failed to delete rule", "error");
    }
  };

  const handleSeniorityBandsSave = async (bands) => {
    try {
      const res = await seniorityBandsApi.save(bands);
      if (res.success) {
        setTenantSeniorityBands(bands);
        showNotification("Seniority bands saved.");
      } else {
        showNotification(
          res.message || "Failed to save seniority bands",
          "error",
        );
      }
    } catch (err) {
      showNotification(
        err.message || "Failed to save seniority bands",
        "error",
      );
    }
    setShowSeniorityModal(false);
  };

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Settings", path: "/settings/distribution-rules" },
    { label: "Distribution Rules", path: "/settings/distribution-rules" },
  ];

  // ── Empty state for global admin without tenant ───────────────────────
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Distribution Rules - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Distribution Rules"
          description="Configure TRONC distribution rules."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to view and manage their distribution rules.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>
          Distribution Rules - {selectedTenant?.name || "Rocket Tronc"}
        </title>
      </Helmet>

      <PageLayout
        title="Distribution Rules"
        description={
          selectedTenant
            ? `Viewing ${selectedTenant.name} — Configure TRONC distribution rules.`
            : "Configure TRONC distribution rules."
        }
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canModifyRules && !showNewForm && (
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleCreateNew}
              >
                Schedule New Rule
              </Button>
            )}
            <Link to="/distribution-lab" className="btn btn-outline">
              <Icon name="FlaskConical" size={16} />
              Test in Lab
            </Link>
            {canModifyRules && (
              <button
                className="btn btn-outline"
                onClick={() => setShowSeniorityModal(true)}
              >
                <Icon name="Award" size={16} />
                Seniority Bands
              </button>
            )}
          </div>
        }
      >
        {/* ── Notification toast ── */}
        {notification && (
          <div
            className={`fixed top-20 right-4 z-[1300] alert ${
              notification.type === "error"
                ? "alert-error"
                : notification.type === "warning"
                  ? "alert-warning"
                  : "alert-success"
            } shadow-lg`}
          >
            <Icon
              name={
                notification.type === "error" ? "AlertCircle" : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-2">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* ── Compliance banner ── */}
        {!loading && canModifyRules && (
          <div className="mb-6 bg-info/5 border border-info/20 rounded-lg p-4 flex items-start gap-3">
            <Icon
              name="ShieldCheck"
              size={20}
              className="text-info mt-0.5 shrink-0"
            />
            <div className="text-sm">
              <p className="font-medium text-info mb-1">
                HMRC Compliance: Distribution Rules Are Versioned
              </p>
              <p className="text-muted-foreground">
                The active rule cannot be edited. To change distribution policy,
                schedule a new rule with a future effective date. Previous rules
                are preserved for audit purposes.
              </p>
            </div>
          </div>
        )}

        {/* ── Loading ── */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* ── Error ── */}
        {error && !loading && (
          <div className="alert alert-error mb-6">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button
              className="btn btn-outline btn-sm ml-auto"
              onClick={refetch}
            >
              <Icon name="RefreshCw" size={16} /> Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-8">
            {/* ══════════════════════════════════════════════════════════
                NEW / EDIT RULE FORM
                ══════════════════════════════════════════════════════════ */}
            {showNewForm && (
              <RuleFormCard
                mode={formMode}
                formData={formData}
                loadingDetail={loadingFormDetail}
                saving={mutationLoading}
                tenantJobRoles={tenantJobRoles}
                jobRolesLoading={jobRolesLoading}
                departments={departments}
                requiresAllocation={requiresAllocation}
                isValidAllocations={isValidAllocations}
                tenantSeniorityBands={tenantSeniorityBands}
                isFirstRule={isFirstNonDraftRule && formMode === "create"}
                onCancel={handleCancelForm}
                onSave={handleSave}
                onChange={handleFormChange}
                onTypeChange={handleTypeChange}
                onUniversalOptionsChange={handleUniversalOptionsChange}
                onAddAllocation={handleAddAllocation}
                onRemoveAllocation={handleRemoveAllocation}
                onAllocationChange={handleAllocationChange}
                onResetMultipliers={resetMultipliers}
                onAllocationsChange={(allocs) =>
                  handleFormChange("allocations", allocs)
                }
                onConfigureSeniority={() => setShowSeniorityModal(true)}
              />
            )}

            {/* ══════════════════════════════════════════════════════════
                ACTIVE RULE
                ══════════════════════════════════════════════════════════ */}
            {activeRule ? (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-lg font-semibold">Active Rule</h2>
                  <span className="badge badge-success text-xs">Live</span>
                </div>
                <div className="card border-2 border-success/30">
                  <div className="card-header bg-success/5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                          <Icon
                            name="ShieldCheck"
                            size={20}
                            className="text-success"
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold">{activeRule.name}</h3>
                          <p className="text-xs text-muted-foreground">
                            {TYPE_NAMES[parseRuleType(activeRule.type)] || "—"}
                            {activeRule.effectiveFrom &&
                              ` · Active since ${formatDate(
                                activeRule.effectiveFrom,
                              )}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon
                          name="Lock"
                          size={16}
                          className="text-muted-foreground"
                          title="Active rules are read-only"
                        />
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleExpandDetail(activeRule.id)}
                        >
                          <Icon
                            name={
                              expandedDetails[activeRule.id] &&
                              expandedDetails[activeRule.id] !== "loading"
                                ? "ChevronUp"
                                : "ChevronDown"
                            }
                            size={16}
                          />
                          {expandedDetails[activeRule.id] &&
                          expandedDetails[activeRule.id] !== "loading"
                            ? "Hide Details"
                            : "View Details"}
                        </button>
                      </div>
                    </div>
                  </div>
                  {expandedDetails[activeRule.id] && (
                    <div className="card-content p-6">
                      {expandedDetails[activeRule.id] === "loading" ? (
                        <div className="flex justify-center py-6">
                          <div className="w-5 h-5 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : (
                        <RuleDetailPanel
                          detail={expandedDetails[activeRule.id]}
                          tenantJobRoles={tenantJobRoles}
                          departments={departments}
                        />
                      )}
                    </div>
                  )}
                </div>
              </section>
            ) : (
              !loading &&
              rules.filter((r) => !r.isDraft).length === 0 && (
                <div className="card p-8 text-center">
                  <Icon
                    name="Settings"
                    size={40}
                    className="mx-auto mb-3 text-muted-foreground"
                  />
                  <h3 className="font-semibold mb-2">
                    No Active Distribution Rule
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    No distribution rule is currently active. Create a rule to
                    define how TRONC is distributed.
                  </p>
                  {canModifyRules && !showNewForm && (
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={handleCreateNew}
                    >
                      Create First Rule
                    </Button>
                  )}
                </div>
              )
            )}

            {/* ══════════════════════════════════════════════════════════
                SCHEDULED RULES
                ══════════════════════════════════════════════════════════ */}
            {scheduledRules.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-lg font-semibold">Scheduled Rules</h2>
                  <span className="badge badge-info text-xs">
                    {scheduledRules.length}
                  </span>
                </div>
                <div className="space-y-4">
                  {scheduledRules.map((rule) => {
                    const isBeingEdited =
                      formMode === "edit-scheduled" && formTargetId === rule.id;
                    return (
                      <div
                        key={rule.id}
                        className={`card border-2 ${
                          isBeingEdited ? "border-primary/40" : "border-info/30"
                        }`}
                      >
                        <div
                          className={`card-header ${
                            isBeingEdited ? "bg-primary/5" : "bg-info/5"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  isBeingEdited ? "bg-primary/10" : "bg-info/10"
                                }`}
                              >
                                <Icon
                                  name="CalendarClock"
                                  size={20}
                                  className={
                                    isBeingEdited ? "text-primary" : "text-info"
                                  }
                                />
                              </div>
                              <div>
                                <h3 className="font-semibold">{rule.name}</h3>
                                <p className="text-xs text-muted-foreground">
                                  {TYPE_NAMES[parseRuleType(rule.type)] || "—"}
                                  {rule.effectiveFrom &&
                                    ` · Takes effect ${formatDate(
                                      rule.effectiveFrom,
                                    )}`}
                                </p>
                              </div>
                            </div>
                            {!isBeingEdited && canModifyRules && (
                              <div className="flex items-center gap-2">
                                <button
                                  className="btn btn-outline btn-sm"
                                  onClick={() =>
                                    openEditForm(rule, "edit-scheduled")
                                  }
                                >
                                  <Icon name="Pencil" size={14} /> Edit
                                </button>
                                <button
                                  className="btn btn-outline btn-sm text-error border-error/30 hover:bg-error/10"
                                  onClick={() => handleDeleteRule(rule)}
                                  title="Delete scheduled rule"
                                >
                                  <Icon name="Trash2" size={14} />
                                </button>
                              </div>
                            )}
                            {isBeingEdited && (
                              <span className="text-xs text-primary font-medium">
                                Editing below ↓
                              </span>
                            )}
                          </div>
                        </div>
                        {!isBeingEdited && (
                          <div className="px-4 pb-4">
                            <button
                              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-2"
                              onClick={() => handleExpandDetail(rule.id)}
                            >
                              <Icon
                                name={
                                  expandedDetails[rule.id] &&
                                  expandedDetails[rule.id] !== "loading"
                                    ? "ChevronUp"
                                    : "ChevronDown"
                                }
                                size={12}
                              />
                              {expandedDetails[rule.id] &&
                              expandedDetails[rule.id] !== "loading"
                                ? "Hide details"
                                : "View details"}
                            </button>
                            {expandedDetails[rule.id] && (
                              <div className="mt-3 pt-3 border-t border-border">
                                {expandedDetails[rule.id] === "loading" ? (
                                  <div className="flex justify-center py-4">
                                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                  </div>
                                ) : (
                                  <RuleDetailPanel
                                    detail={expandedDetails[rule.id]}
                                    tenantJobRoles={tenantJobRoles}
                                    departments={departments}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ══════════════════════════════════════════════════════════
                DRAFT RULES
                ══════════════════════════════════════════════════════════ */}
            {draftRules.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-lg font-semibold">Draft Rules</h2>
                  <span className="badge badge-warning text-xs">
                    {draftRules.length}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    — For testing only, not used in distributions
                  </span>
                </div>
                <div className="space-y-3">
                  {draftRules.map((rule) => {
                    const isBeingEdited =
                      formMode === "edit-draft" && formTargetId === rule.id;
                    return (
                      <div
                        key={rule.id}
                        className={`card border ${
                          isBeingEdited
                            ? "border-primary/40"
                            : "border-warning/30 bg-warning/5"
                        }`}
                      >
                        <div className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center">
                              <Icon
                                name="FlaskConical"
                                size={16}
                                className="text-warning"
                              />
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {rule.name}
                                <span className="badge badge-warning text-xs ml-2">
                                  Draft
                                </span>
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {TYPE_NAMES[parseRuleType(rule.type)] || "—"}
                              </p>
                            </div>
                          </div>
                          {!isBeingEdited && canModifyRules && (
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/distribution-lab?ruleId=${rule.id}`}
                                className="btn btn-outline btn-sm"
                              >
                                <Icon name="FlaskConical" size={14} /> Test
                              </Link>
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => openEditForm(rule, "edit-draft")}
                              >
                                <Icon name="Pencil" size={14} /> Edit
                              </button>
                              <button
                                className="btn btn-outline btn-sm text-error border-error/30 hover:bg-error/10"
                                onClick={() => handleDeleteRule(rule)}
                              >
                                <Icon name="Trash2" size={14} />
                              </button>
                            </div>
                          )}
                          {isBeingEdited && (
                            <span className="text-xs text-primary font-medium">
                              Editing below ↓
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ══════════════════════════════════════════════════════════
                PREVIOUS RULES (history)
                ══════════════════════════════════════════════════════════ */}
            {previousRules.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-lg font-semibold">Previous Rules</h2>
                  <span className="badge badge-neutral text-xs">
                    {previousRules.length}
                  </span>
                </div>
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">
                            Strategy
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">
                            Active Period
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {previousRules.map((rule) => (
                          <React.Fragment key={rule.id}>
                            <tr className="hover:bg-muted/30">
                              <td className="px-4 py-3 text-sm font-medium">
                                {rule.name}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {TYPE_NAMES[parseRuleType(rule.type)] || "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {formatDate(rule.effectiveFrom)}
                                {rule.effectiveTo && (
                                  <> – {formatDate(rule.effectiveTo)}</>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {rule.hasBeenDistributed ? (
                                  <span className="badge badge-neutral text-xs">
                                    Used in Distributions
                                  </span>
                                ) : (
                                  <span className="badge badge-neutral text-xs">
                                    Superseded
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                  onClick={() => handleExpandDetail(rule.id)}
                                >
                                  <Icon
                                    name={
                                      expandedDetails[rule.id] &&
                                      expandedDetails[rule.id] !== "loading"
                                        ? "ChevronUp"
                                        : "Eye"
                                    }
                                    size={14}
                                  />
                                  {expandedDetails[rule.id] &&
                                  expandedDetails[rule.id] !== "loading"
                                    ? "Hide"
                                    : "View"}
                                </button>
                              </td>
                            </tr>
                            {expandedDetails[rule.id] && (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="px-4 py-4 bg-muted/20"
                                >
                                  {expandedDetails[rule.id] === "loading" ? (
                                    <div className="flex justify-center py-4">
                                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                    </div>
                                  ) : (
                                    <RuleDetailPanel
                                      detail={expandedDetails[rule.id]}
                                      tenantJobRoles={tenantJobRoles}
                                      departments={departments}
                                    />
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {/* ── Empty state (no rules at all) ── */}
            {!loading && rules.length === 0 && !showNewForm && (
              <div className="card p-12 text-center">
                <Icon
                  name="Scale"
                  size={48}
                  className="mx-auto mb-4 text-muted-foreground"
                />
                <h3 className="text-lg font-semibold mb-2">
                  No Distribution Rules Yet
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Distribution rules define how TRONC is allocated among
                  eligible employees. Create your first rule to get started.
                </p>
                {canModifyRules && (
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={handleCreateNew}
                  >
                    Create First Rule
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Seniority Bands Modal ── */}
        {showSeniorityModal && (
          <SeniorityBandsModal
            isOpen={showSeniorityModal}
            bands={tenantSeniorityBands}
            onSave={handleSeniorityBandsSave}
            onClose={() => setShowSeniorityModal(false)}
          />
        )}
      </PageLayout>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RuleFormCard — inline form for creating or editing a scheduled/draft rule
// ─────────────────────────────────────────────────────────────────────────────
const RuleFormCard = ({
  mode,
  formData,
  loadingDetail,
  saving,
  tenantJobRoles,
  jobRolesLoading,
  departments,
  requiresAllocation,
  isValidAllocations,
  tenantSeniorityBands,
  isFirstRule,
  onCancel,
  onSave,
  onChange,
  onTypeChange,
  onUniversalOptionsChange,
  onAddAllocation,
  onRemoveAllocation,
  onAllocationChange,
  onResetMultipliers,
  onAllocationsChange,
  onConfigureSeniority,
}) => {
  const isDraft = formData.isDraft;
  const isCreate = mode === "create";

  const titleMap = {
    create: "Schedule New Rule",
    "edit-scheduled": "Edit Scheduled Rule",
    "edit-draft": "Edit Draft Rule",
  };

  const isEffectiveDateValid = () => {
    if (isDraft || isFirstRule) return true;
    if (!formData.effectiveFrom) return false;
    const sel = new Date(formData.effectiveFrom);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return sel > today;
  };

  if (loadingDetail) {
    return (
      <div className="card border-2 border-primary/30 p-8 text-center">
        <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Loading rule details...</p>
      </div>
    );
  }

  return (
    <div className="card border-2 border-primary/30">
      {/* Header */}
      <div className="card-header bg-primary/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon
                name={isCreate ? "Plus" : "Pencil"}
                size={20}
                className="text-primary"
              />
            </div>
            <div>
              <h3 className="font-semibold">{titleMap[mode] || "Rule"}</h3>
              <p className="text-xs text-muted-foreground">
                {isDraft
                  ? "Draft — for testing in the Lab, not used in distributions"
                  : isFirstRule && isCreate
                    ? "Will activate immediately (first rule)"
                    : "Will take effect on the specified date"}
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
      </div>

      <div className="card-content space-y-6 p-6">
        {/* Draft toggle — only for create or edit-draft */}
        {(isCreate || mode === "edit-draft") && (
          <div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isDraft}
                onChange={(e) => {
                  onChange("isDraft", e.target.checked);
                  if (!e.target.checked && !isFirstRule) {
                    if (!formData.effectiveFrom) {
                      onChange("effectiveFrom", getTomorrow());
                    }
                  }
                }}
                className="checkbox"
              />
              <div>
                <span className="font-medium">Draft Rule</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Draft rules can be tested in the Lab but won't be used for
                  actual distributions.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Effective From Date */}
        {!isDraft && (
          <div>
            {isFirstRule && isCreate ? (
              <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Zap" size={16} className="text-success" />
                  <span className="font-medium text-success">
                    First Rule — Activates Immediately
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  This is your first distribution rule. It will be activated
                  immediately with today's date. No effective date is required.
                </p>
              </div>
            ) : (
              <div className="p-4 bg-info/5 border border-info/20 rounded-lg">
                <label className="label flex items-center gap-2 mb-2">
                  <Icon name="Calendar" size={16} className="text-info" />
                  Effective From Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={formData.effectiveFrom || ""}
                  onChange={(e) => onChange("effectiveFrom", e.target.value)}
                  min={getTomorrow()}
                  className="input w-full max-w-xs"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must be a future date. The current active rule will expire the
                  day before.
                </p>
                {formData.effectiveFrom && !isEffectiveDateValid() && (
                  <p className="text-xs text-error mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    Effective date must be in the future
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Name */}
        <div>
          <label className="label">
            Rule Name <span className="text-error">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => onChange("name", e.target.value)}
            className="input"
            placeholder="e.g., Q1 2025 Equal Distribution"
          />
        </div>

        {/* Description */}
        <div>
          <label className="label">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => onChange("description", e.target.value)}
            className="input"
            rows={2}
            placeholder="Optional description..."
          />
        </div>

        {/* Distribution Strategy */}
        <div>
          <label className="label mb-3">
            Distribution Strategy <span className="text-error">*</span>
          </label>
          <RuleTypeSelector
            selectedType={formData.type}
            onSelect={onTypeChange}
            showDescriptions={true}
          />
        </div>

        {/* Allocations */}
        {requiresAllocation && (
          <AllocationEditor
            formData={formData}
            tenantJobRoles={tenantJobRoles}
            jobRolesLoading={jobRolesLoading}
            departments={departments}
            isValidAllocations={isValidAllocations}
            onAddAllocation={onAddAllocation}
            onRemoveAllocation={onRemoveAllocation}
            onAllocationChange={onAllocationChange}
            onResetMultipliers={onResetMultipliers}
            onAllocationsChange={onAllocationsChange}
          />
        )}

        {/* Universal Options */}
        <UniversalOptionsPanel
          options={{
            enableMinimumFloor: formData.enableMinimumFloor,
            minimumFloorAmount: formData.minimumFloorAmount,
            enableHourFactor: formData.enableHourFactor,
            hourFactorWeight: formData.hourFactorWeight,
            enableSeniorityFactor: formData.enableSeniorityFactor,
            seniorityFactorWeight: formData.seniorityFactorWeight,
            seniorityBands: tenantSeniorityBands,
          }}
          onChange={onUniversalOptionsChange}
          onConfigureSeniority={onConfigureSeniority}
        />

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="default"
            iconName="Save"
            iconPosition="left"
            onClick={onSave}
            disabled={
              saving ||
              (!isDraft && !isFirstRule && !isEffectiveDateValid()) ||
              (requiresAllocation && !isValidAllocations)
            }
            loading={saving}
          >
            {saving
              ? "Saving..."
              : isCreate
                ? isDraft
                  ? "Create Draft"
                  : isFirstRule
                    ? "Create & Activate"
                    : "Schedule Rule"
                : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AllocationEditor — allocation config inside RuleFormCard
// ─────────────────────────────────────────────────────────────────────────────
const AllocationEditor = ({
  formData,
  tenantJobRoles,
  jobRolesLoading,
  departments,
  isValidAllocations,
  onAddAllocation,
  onRemoveAllocation,
  onAllocationChange,
  onResetMultipliers,
  onAllocationsChange,
}) => {
  const type = formData.type;
  const label = type === 1 ? "Job Role Allocations" : "Department Allocations";

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="label">
          {label} <span className="text-error">*</span>
        </label>
        <span className="text-sm text-muted-foreground">
          {formData.allocations.length} configured
        </span>
      </div>

      {/* Job Role (type 1) */}
      {type === 1 && (
        <>
          {jobRolesLoading ? (
            <div className="p-4 text-center">
              <div className="spinner mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Loading job roles...
              </p>
            </div>
          ) : tenantJobRoles.length === 0 ? (
            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon
                  name="AlertTriangle"
                  size={16}
                  className="text-warning shrink-0 mt-0.5"
                />
                <p className="text-sm text-warning">
                  No job roles found.{" "}
                  <Link to="/system/job-roles" className="underline">
                    Create job roles first →
                  </Link>
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    const existingIds = new Set(
                      formData.allocations.map((a) => a.jobRoleId),
                    );
                    const newAllocs = tenantJobRoles
                      .filter((r) => !existingIds.has(r.id))
                      .map((r, idx) => ({
                        jobRoleId: r.id,
                        scoreMultiplier: 1.0,
                        sortOrder: formData.allocations.length + idx,
                      }));
                    if (newAllocs.length === 0) return;
                    onAllocationsChange([
                      ...formData.allocations,
                      ...newAllocs,
                    ]);
                  }}
                >
                  <Icon name="PlusCircle" size={16} /> Add All Roles
                </button>
                {formData.allocations.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={onResetMultipliers}
                    >
                      <Icon name="RotateCcw" size={16} /> Reset ×1.0
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm text-error border-error/30 hover:bg-error/10"
                      onClick={() => onAllocationsChange([])}
                    >
                      <Icon name="XCircle" size={16} /> Remove All
                    </button>
                  </>
                )}
              </div>
              <div className="space-y-2">
                {tenantJobRoles.map((role) => {
                  const allocIdx = formData.allocations.findIndex(
                    (a) => a.jobRoleId === role.id,
                  );
                  const isAdded = allocIdx !== -1;
                  const alloc = isAdded ? formData.allocations[allocIdx] : null;
                  return (
                    <div
                      key={role.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        isAdded
                          ? "bg-primary/5 border-primary/30"
                          : "bg-muted/30 border-border"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (isAdded) {
                            onRemoveAllocation(allocIdx);
                          } else {
                            onAllocationsChange([
                              ...formData.allocations,
                              {
                                jobRoleId: role.id,
                                scoreMultiplier: 1.0,
                                sortOrder: formData.allocations.length,
                              },
                            ]);
                          }
                        }}
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                          isAdded
                            ? "bg-primary text-white hover:bg-primary/80"
                            : "bg-muted text-muted-foreground hover:bg-muted-foreground/20"
                        }`}
                      >
                        <Icon name={isAdded ? "Check" : "Plus"} size={16} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-medium text-sm ${
                              isAdded ? "" : "text-muted-foreground"
                            }`}
                          >
                            {role.name}
                          </span>
                          {role.department && (
                            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              {role.department}
                            </span>
                          )}
                          {role.isManagement && (
                            <span className="text-xs text-warning bg-warning/10 px-1.5 py-0.5 rounded">
                              Mgmt
                            </span>
                          )}
                        </div>
                      </div>
                      {isAdded && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <input
                            type="number"
                            value={alloc.scoreMultiplier ?? 1.0}
                            onChange={(e) =>
                              onAllocationChange(
                                allocIdx,
                                "scoreMultiplier",
                                parseFloat(e.target.value) || 1.0,
                              )
                            }
                            className="input w-20 text-center text-sm"
                            min="0.01"
                            step="0.1"
                          />
                          <span className="text-sm text-muted-foreground">
                            ×
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {formData.allocations.length > 0 && (
                <div className="mt-3 p-3 rounded-lg border text-sm bg-success/5 border-success/20 text-success">
                  {formData.allocations.length} of {tenantJobRoles.length} roles
                  selected
                </div>
              )}
              {formData.allocations.length === 0 && (
                <div className="mt-3 p-4 text-center bg-muted/30 rounded-lg border border-dashed border-border">
                  <Icon
                    name="PlusCircle"
                    size={24}
                    className="mx-auto mb-2 text-muted-foreground"
                  />
                  <p className="text-sm text-muted-foreground">
                    Click the <strong>+</strong> to include a role, or use{" "}
                    <strong>Add All Roles</strong>.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* By Department (type 2) */}
      {type === 2 && (
        <>
          {departments.length === 0 ? (
            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon
                  name="AlertTriangle"
                  size={16}
                  className="text-warning shrink-0 mt-0.5"
                />
                <p className="text-sm text-warning">
                  No departments found. Please configure departments first.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    const existingIds = new Set(
                      formData.allocations.map((a) => a.department),
                    );
                    const newAllocs = departments
                      .filter((d) => !existingIds.has(d.id))
                      .map((d, idx) => ({
                        department: d.id,
                        scoreMultiplier: 1.0,
                        sortOrder: formData.allocations.length + idx,
                      }));
                    if (newAllocs.length === 0) return;
                    onAllocationsChange([
                      ...formData.allocations,
                      ...newAllocs,
                    ]);
                  }}
                >
                  <Icon name="PlusCircle" size={16} /> Add All Departments
                </button>
                {formData.allocations.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={onResetMultipliers}
                    >
                      <Icon name="RotateCcw" size={16} /> Reset ×1.0
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm text-error border-error/30 hover:bg-error/10"
                      onClick={() => onAllocationsChange([])}
                    >
                      <Icon name="XCircle" size={16} /> Remove All
                    </button>
                  </>
                )}
              </div>
              <div className="space-y-2">
                {departments.map((dept) => {
                  const allocIdx = formData.allocations.findIndex(
                    (a) => a.department === dept.id,
                  );
                  const isAdded = allocIdx !== -1;
                  const alloc = isAdded ? formData.allocations[allocIdx] : null;
                  return (
                    <div
                      key={dept.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        isAdded
                          ? "bg-primary/5 border-primary/30"
                          : "bg-muted/30 border-border"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (isAdded) {
                            onRemoveAllocation(allocIdx);
                          } else {
                            onAllocationsChange([
                              ...formData.allocations,
                              {
                                department: dept.id,
                                scoreMultiplier: 1.0,
                                sortOrder: formData.allocations.length,
                              },
                            ]);
                          }
                        }}
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                          isAdded
                            ? "bg-primary text-white hover:bg-primary/80"
                            : "bg-muted text-muted-foreground hover:bg-muted-foreground/20"
                        }`}
                      >
                        <Icon name={isAdded ? "Check" : "Plus"} size={16} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <span
                          className={`font-medium text-sm ${
                            isAdded ? "" : "text-muted-foreground"
                          }`}
                        >
                          {dept.name}
                        </span>
                      </div>
                      {isAdded && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <input
                            type="number"
                            value={alloc.scoreMultiplier ?? 1.0}
                            onChange={(e) =>
                              onAllocationChange(
                                allocIdx,
                                "scoreMultiplier",
                                parseFloat(e.target.value) || 1.0,
                              )
                            }
                            className="input w-20 text-center text-sm"
                            min="0.01"
                            step="0.1"
                          />
                          <span className="text-sm text-muted-foreground">
                            ×
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {formData.allocations.length > 0 && (
                <div className="mt-3 p-3 rounded-lg border text-sm bg-success/5 border-success/20 text-success">
                  {formData.allocations.length} of {departments.length}{" "}
                  departments selected
                </div>
              )}
              {formData.allocations.length === 0 && (
                <div className="mt-3 p-4 text-center bg-muted/30 rounded-lg border border-dashed border-border">
                  <Icon
                    name="PlusCircle"
                    size={24}
                    className="mx-auto mb-2 text-muted-foreground"
                  />
                  <p className="text-sm text-muted-foreground">
                    Click the <strong>+</strong> to include a department, or use{" "}
                    <strong>Add All Departments</strong>.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RuleDetailPanel — read-only expanded detail for active / previous / scheduled
// ─────────────────────────────────────────────────────────────────────────────
const RuleDetailPanel = ({ detail, tenantJobRoles, departments }) => {
  if (!detail) return null;

  const type = parseRuleType(detail.type);
  const allocations = detail.allocations || [];
  const hasAllocations =
    TYPES_REQUIRING_ALLOCATION.includes(type) && allocations.length > 0;

  const resolveRoleName = (jobRoleId) => {
    const role = tenantJobRoles.find((r) => r.id === jobRoleId);
    return role ? role.name : jobRoleId || "—";
  };

  const resolveDeptName = (deptId) => {
    const d = departments.find((d) => d.id === deptId);
    return d ? d.name : deptId || "—";
  };

  return (
    <div className="space-y-4">
      {detail.description && (
        <p className="text-sm text-muted-foreground">{detail.description}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {hasAllocations && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {type === 1 ? "Job Role Allocations" : "Department Allocations"}
            </h4>
            <div className="space-y-2">
              {allocations.map((a, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-2.5 bg-muted/40 rounded-lg text-sm"
                >
                  <span>
                    {type === 2
                      ? resolveDeptName(a.department)
                      : resolveRoleName(a.jobRoleId)}
                  </span>
                  <span className="font-semibold text-primary">
                    {parseFloat(a.scoreMultiplier).toFixed(2)}×
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Options
          </h4>
          <div className="space-y-2">
            <OptionBadge
              label="Minimum Floor"
              enabled={detail.enableMinimumFloor}
              value={
                detail.enableMinimumFloor
                  ? `£${detail.minimumFloorAmount}`
                  : null
              }
            />
            <OptionBadge
              label="Hour Factor"
              enabled={detail.enableHourFactor}
              value={
                detail.enableHourFactor ? `${detail.hourFactorWeight}×` : null
              }
            />
            <OptionBadge
              label="Seniority Factor"
              enabled={detail.enableSeniorityFactor}
              value={
                detail.enableSeniorityFactor
                  ? `${detail.seniorityFactorWeight}×`
                  : null
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// OptionBadge
// ─────────────────────────────────────────────────────────────────────────────
const OptionBadge = ({ label, enabled, value }) => (
  <div
    className={`flex items-center justify-between p-2.5 rounded-lg text-sm border ${
      enabled
        ? "bg-success/5 border-success/20 text-success"
        : "bg-muted border-border text-muted-foreground"
    }`}
  >
    <div className="flex items-center gap-2">
      <Icon name={enabled ? "Check" : "X"} size={13} />
      <span>{label}</span>
    </div>
    {value && <span className="font-semibold">{value}</span>}
  </div>
);

export default RuleManager;
